/**
 * SchematicFloorplan — conceptual gold-on-dark SVG "floorplan" illustration.
 * Not a real plan; purely editorial. Gold strokes render a stylized
 * architectural grid with labeled zones.
 */
export default function SchematicFloorplan({ title, caption }) {
    return (
        <figure
            className="border border-[rgba(201,168,106,0.2)] bg-[#0B0B0C] overflow-hidden"
            data-testid="private-schematic"
        >
            <div className="relative bg-[#0B0B0C]">
                <svg
                    viewBox="0 0 1200 600"
                    className="w-full h-auto block"
                    xmlns="http://www.w3.org/2000/svg"
                    role="img"
                    aria-label={title}
                >
                    {/* Subtle grid backdrop */}
                    <defs>
                        <pattern
                            id="gridlines"
                            width="40"
                            height="40"
                            patternUnits="userSpaceOnUse"
                        >
                            <path
                                d="M 40 0 L 0 0 0 40"
                                fill="none"
                                stroke="rgba(201,168,106,0.05)"
                                strokeWidth="1"
                            />
                        </pattern>
                    </defs>
                    <rect width="1200" height="600" fill="#0B0B0C" />
                    <rect width="1200" height="600" fill="url(#gridlines)" />

                    {/* Outer envelope */}
                    <rect
                        x="70"
                        y="70"
                        width="1060"
                        height="460"
                        fill="none"
                        stroke="#C9A86A"
                        strokeWidth="1.4"
                        opacity="0.85"
                    />
                    {/* Inner partitions */}
                    <line x1="470" y1="70" x2="470" y2="530" stroke="#C9A86A" strokeWidth="1" opacity="0.7" />
                    <line x1="800" y1="70" x2="800" y2="530" stroke="#C9A86A" strokeWidth="1" opacity="0.7" />
                    <line x1="470" y1="300" x2="800" y2="300" stroke="#C9A86A" strokeWidth="1" opacity="0.55" />
                    <line x1="800" y1="220" x2="1130" y2="220" stroke="#C9A86A" strokeWidth="1" opacity="0.55" />
                    <line x1="800" y1="400" x2="1130" y2="400" stroke="#C9A86A" strokeWidth="1" opacity="0.55" />

                    {/* Door openings */}
                    <line x1="260" y1="70" x2="330" y2="70" stroke="#0B0B0C" strokeWidth="3" />
                    <line x1="630" y1="300" x2="700" y2="300" stroke="#0B0B0C" strokeWidth="3" />
                    <line x1="470" y1="430" x2="540" y2="430" stroke="#0B0B0C" strokeWidth="3" />

                    {/* Core equipment symbol */}
                    <g opacity="0.9">
                        <circle cx="140" cy="460" r="18" fill="none" stroke="#C9A86A" strokeWidth="1.4" />
                        <circle cx="140" cy="460" r="6" fill="#C9A86A" />
                    </g>

                    {/* Keypad dots */}
                    {[
                        [170, 110],
                        [410, 150],
                        [280, 260],
                        [540, 130],
                        [720, 240],
                        [720, 460],
                        [870, 130],
                        [960, 270],
                        [1050, 450],
                    ].map(([cx, cy]) => (
                        <g key={`${cx}-${cy}`}>
                            <circle cx={cx} cy={cy} r="4" fill="#C9A86A" />
                            <circle cx={cx} cy={cy} r="9" fill="none" stroke="#C9A86A" strokeWidth="0.7" opacity="0.5" />
                        </g>
                    ))}

                    {/* Labels */}
                    <g
                        fill="#C9A86A"
                        fontFamily="monospace"
                        fontSize="10"
                        letterSpacing="1.5"
                    >
                        <text x="90" y="104">A · PRINCIPAL</text>
                        <text x="490" y="104">B · COMMONS</text>
                        <text x="490" y="334">C · THEATRE</text>
                        <text x="820" y="104">D · CORRIDOR</text>
                        <text x="820" y="254">E · RACKS</text>
                        <text x="820" y="434">F · SERVICE</text>
                        <text x="170" y="450" opacity="0.85">CORE</text>
                    </g>

                    {/* Scale / north marker */}
                    <g
                        fill="#9A958A"
                        fontFamily="monospace"
                        fontSize="9"
                        letterSpacing="2"
                    >
                        <text x="1050" y="560">N →</text>
                        <text x="70" y="560">0  ·  5m  ·  10m</text>
                        <line x1="70" y1="550" x2="160" y2="550" stroke="#C9A86A" strokeWidth="0.8" opacity="0.6" />
                    </g>
                </svg>
            </div>
            <figcaption className="px-6 md:px-10 py-5 border-t border-[rgba(201,168,106,0.15)]">
                <div className="text-[10.5px] tracking-[0.32em] uppercase text-[#C9A86A] mb-1.5">
                    {title}
                </div>
                <p className="text-[12.5px] text-[#9A958A] leading-[1.7] italic font-serif">
                    {caption}
                </p>
            </figcaption>
        </figure>
    );
}
